import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Check, RotateCcw } from "lucide-react";
import { DOORWAYS } from "../../data/homeContent";
import { DOORWAY_ROUTE_BY_ID as ROUTES } from "../../data/doorwayContent";

// 1.7 + Task List §16 — DECISION LOCKED (Option 1): the Starting Point selector.
// Opens from "Find My Starting Point", the Discovery Popup doors, and every
// Doorway card. A clicked Doorway arrives pre-selected ("You selected …"),
// can be changed, and confirm routes via simple if/then — no personality
// assessment this sprint.
export const StartingPointSelector = ({ open, onClose, initialDoorway }) => {
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setSelected(initialDoorway || null);
  }, [open, initialDoorway]);

  const close = () => onClose();

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && open) close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const confirm = () => {
    if (!selected) return;
    const to = ROUTES[selected];
    close();
    if (to) navigate(to);
  };

  const doorway = DOORWAYS.find((d) => d.id === selected);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="starting-point-modal"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-md"
          onClick={close}
        >
          <motion.div
            initial={{ y: 36, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-3xl bg-[#0B1020]/95 ring-gold-frame grain"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aurora-weave slow" />
            <button data-testid="starting-point-close" onClick={close}
              className="absolute top-4 right-4 z-20 text-cream/60 hover:text-[#E7CC86] transition-colors"><X size={22} /></button>

            <div className="relative z-10 px-5 sm:px-9 pt-8 pb-7">
              {!doorway ? (
                <>
                  <p className="text-xs tracking-[0.3em] uppercase text-[#E7CC86]/75 text-center mb-3">Your Starting Point</p>
                  <h3 className="font-display font-bold text-3xl sm:text-4xl text-cream leading-tight text-center mb-2">
                    Where does this work <span className="text-gold">meet you?</span>
                  </h3>
                  <p className="text-cream/65 text-center text-[15px] mb-8">Find the doorway that speaks to where you are now.</p>

                  <div className="grid sm:grid-cols-2 gap-3">
                    {DOORWAYS.map((d, i) => (
                      <motion.button
                        key={d.id}
                        data-testid={`selector-door-${d.id}`}
                        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 + i * 0.05 }}
                        onClick={() => setSelected(d.id)}
                        className={`text-left rounded-2xl border p-4 transition-all group ${selected === d.id ? "border-[#C9A24B] bg-[#C9A24B]/10" : "border-[#C9A24B]/20 hover:border-[#C9A24B]/60 hover:bg-white/[0.03]"}`}
                      >
                        <span className="flex items-center gap-2 text-[11px] tracking-[0.22em] text-[#E7CC86]/70">
                          {d.n}
                          <d.icon size={16} strokeWidth={1.5} className="text-[#E7CC86]/75" aria-hidden="true" />
                        </span>
                        <p className="font-display font-semibold text-lg text-cream leading-tight mt-0.5 flex items-center justify-between gap-2">
                          {d.title}
                          <ArrowRight size={17} className="text-[#E7CC86] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all shrink-0" />
                        </p>
                        <p className="font-serif italic text-cream/55 text-sm mt-1 leading-snug">“{d.intent}”</p>
                      </motion.button>
                    ))}
                  </div>
                </>
              ) : (
                <motion.div key="confirm" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
                  <p className="text-xs tracking-[0.3em] uppercase text-[#E7CC86]/75 text-center mb-4">Your Starting Point</p>
                  <div className="max-w-xl mx-auto text-center">
                    <div className="w-12 h-12 rounded-full btn-gold flex items-center justify-center mx-auto mb-5">
                      <Check size={24} className="text-[#14100A]" strokeWidth={3} />
                    </div>
                    <h3 className="font-display font-bold text-2xl sm:text-3xl text-cream mb-3">
                      You selected <span className="text-gold">{doorway.title}</span>
                    </h3>
                    <p className="font-serif italic text-[#E7CC86] text-lg mb-3">“{doorway.intent}”</p>
                    <p className="text-cream/70 leading-relaxed mb-8">{doorway.body}</p>

                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <button data-testid="selector-confirm" onClick={confirm}
                        className="btn-gold px-8 py-3.5 rounded-full font-semibold inline-flex items-center justify-center gap-2">
                        Continue to {doorway.title} <ArrowRight size={18} />
                      </button>
                      <button data-testid="selector-change" onClick={() => setSelected(null)}
                        className="btn-ghost-gold px-6 py-3.5 rounded-full font-medium inline-flex items-center justify-center gap-2">
                        <RotateCcw size={16} /> Choose a different doorway
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
